import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MotionConfig, motion, useMotionValueEvent, useScroll } from "motion/react";
import { SkedMark } from "../components/SkedMark";
import { CountUp } from "../components/motion/CountUp";
import { Reveal } from "../components/motion/Reveal";
import { EASE, staggerContainer, staggerItem } from "../components/motion/tokens";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { usePageMeta } from "../hooks/usePageMeta";
import { withBase } from "../lib/base-path";
import { reportTelemetry } from "../lib/telemetry";
import { captureFirstTouch } from "../lib/attribution";
import { claimSessionToken, pushGtmEvent } from "../lib/gtm-events";
import {
  SAMPLE_HEADLINE,
  SAMPLE_HEADLINE_METRICS,
  SAMPLE_REPORT_LABEL,
  samplePlatformBars,
} from "../../shared/report/sample-headline";
import { buildSkedHref } from "../../shared/leadgen/cta-rules";
import { LANDING_FAQ } from "../../shared/marketing/landing-faq";

const LANDING_MARQUEE_LOGO_HREF = buildSkedHref({
  medium: "landing",
  campaign: "marquee",
  content: "logo",
});
const LANDING_MARQUEE_LEARN_MORE_HREF = buildSkedHref({
  medium: "landing",
  campaign: "marquee",
  content: "learn_more_link",
});
const LANDING_MID_EXPLORE_HREF = buildSkedHref({
  medium: "landing",
  campaign: "mid_cta",
  content: "explore_button",
});
const LANDING_FINAL_CTA_PRICING_HREF = buildSkedHref({
  medium: "landing",
  campaign: "final_cta",
  content: "pricing_link",
  path: "/pricing",
});
const LANDING_SITE_FOOTER_HREF = buildSkedHref({
  medium: "landing",
  campaign: "site_footer",
});

// Agency-section CTA is an internal link into the connect flow, but it
// carries its own utm_campaign so GA4 can split agency-driven connects
// from the hero CTA (handover item 2 acceptance).
const LANDING_AGENCY_APP_LINK =
  "/app?utm_source=traffic-snapshot&utm_medium=landing&utm_campaign=agency_section";

// Item 8 (launch hardening) — flag-gated Product Hunt strip. Flip to
// true on launch day (Sep 1, Wk 8) once Lach's assets land; renders
// nothing until then. Keep the copy + href in this one place.
const SHOW_PRODUCT_HUNT_STRIP = false;
const PRODUCT_HUNT_URL = "https://www.producthunt.com/"; // TODO(launch): real listing URL

// Ported from the 2026-04 design handoff
// (`docs/design-system/reference/homepage.html`).
// - Blurple (--primary, #4F31FF) is the primary CTA, the links and the accent word.
// - Gotham SSm Black 900 for marketing display headlines.
// - Quarto italic for editorial eyebrows, pull-quotes, and single-word accents.
// - Three "stages": white page → peach (About-Sked) → lavender wash (UTM section) → ink (final CTA band).
// - Cards and metrics use subtle neutral-100 borders, not the rings pattern from the app shell.

export function LandingPage() {
  usePageMeta({
    title: "Traffic Snapshot: the social-first GA4 read by Sked Social",
    description:
      "Connect GA4 and get a social-first traffic report in about a minute, plus a UTM Health score that flags broken attribution. Free, from Sked Social.",
  });
  // First-touch attribution. The landing page is the usual entry for
  // paid traffic, so this is where the ad's UTMs + click IDs get frozen
  // for the tab session; ConnectPage replays them onto the auth URL.
  // First write wins — see captureFirstTouch.
  useEffect(() => {
    captureFirstTouch();
  }, []);
  // Handover 3.5 — forwarded-digest visibility. Digest emails stamp
  // ?ref=digest-forward on their tool links; a landing load carrying it
  // means a forwarded email got clicked. Anonymous-allowed server-side;
  // once per tab session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("ref") !== "digest-forward") return;
    if (!claimSessionToken("ts.digest_forward.session")) return;
    reportTelemetry({ event: "digest_forward_clicked", tags: { ref: "digest-forward" } });
    pushGtmEvent("digest_forward_clicked");
  }, []);
  return (
    // reducedMotion="user" → Motion skips transform/scale animations (jumping to
    // their final value) while still fading opacity, for anyone with
    // prefers-reduced-motion. Scoped to the landing page so in-app screens are
    // untouched.
    <MotionConfig reducedMotion="user">
      <div className="min-h-dvh bg-white text-ink antialiased">
        <SiteNav />
        <ProductHuntStrip />
        <Hero />
        <Marquee />
        <AboutSked />
        <ThreeReads />
        <UtmHealthStage />
        <AgencySection />
        <FaqSection />
        <FinalCtaBand />
        <SiteFooter />
      </div>
    </MotionConfig>
  );
}

function SiteNav() {
  // Subtle elevation once the page scrolls — the bar settles from a flat hairline
  // into a faint shadow so it reads as "lifted above the content."
  const { scrollY } = useScroll();
  const [elevated, setElevated] = useState(false);
  useMotionValueEvent(scrollY, "change", (v) => setElevated(v > 8));
  return (
    <nav
      className={
        "sticky top-0 z-20 border-b bg-white/85 backdrop-blur transition-shadow duration-200 " +
        (elevated
          ? "border-neutral-950/10 shadow-[0_2px_10px_-2px_rgba(16,24,40,0.10)]"
          : "border-neutral-950/5 shadow-none")
      }
    >
      <div className="mx-auto flex max-w-[1440px] items-center gap-4 px-5 py-4 md:gap-9 md:px-8">
        <Link to="/" className="flex items-center gap-3 text-ink no-underline">
          <SkedMark className="h-10 w-10" aria-hidden="true" />
          <span className="leading-[1.05]">
            <span className="block text-xs font-bold tracking-tight text-neutral-600">
              Sked Social
            </span>
            <span className="block font-display text-lg font-[900] tracking-tight text-ink">
              Traffic Snapshot
            </span>
          </span>
        </Link>
        <div className="hidden items-center gap-7 text-sm font-medium text-neutral-700 md:flex">
          <a className="hover:text-purple-700" href="#cuts">
            Features
          </a>
          <a className="hover:text-purple-700" href="#health">
            UTM Health
          </a>
          <a className="hover:text-purple-700" href="#agencies">
            For agencies
          </a>
        </div>
        <div className="flex-1" />
        <Link to="/app" className="ts-cta">
          Get free report <span className="ts-cta-arrow">→</span>
        </Link>
      </div>
    </nav>
  );
}

function ShieldCheckIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 5 6v5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function Hero() {
  return (
    <header
      className="relative overflow-hidden bg-white py-16 pb-22 md:pb-24"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 110% 20%, #EEE9FF 0%, transparent 55%), radial-gradient(900px 420px at -10% 90%, #FDF3E8 0%, transparent 60%), #fff",
      }}
    >
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-8 md:grid-cols-[1.1fr_0.9fr]">
        <div>
          {/* SKE-3438 item 3 — eyebrow now sells "free + 1 min" instead
           *  of the editorial "A report, not another dashboard." line.
           *  The headline can carry the brand voice; the eyebrow's job
           *  is to address the cold visitor's first two questions
           *  (cost + time-to-value) before they read anything else. */}
          <p className="ts-reveal text-[15px] font-semibold tracking-[-0.005em] text-purple-700">
            Free Google Analytics audit · 1-minute setup
          </p>
          <h1
            className="ts-reveal mt-[18px] text-balance font-display text-[48px] font-[900] leading-[1.02] tracking-[-0.03em] text-ink md:text-[60px]"
            style={{ animationDelay: "70ms" }}
          >
            See what social{" "}
            <span className="font-serif italic font-semibold tracking-[-0.015em] text-purple-500">
              actually
            </span>{" "}
            drives on-site.
          </h1>
          {/* SKE-3438 items 1 + 4 collapsed into a single body block.
           *  The eyebrow already carries "free + 1 minute"; the H1
           *  carries the brand voice. This paragraph carries the
           *  differentiator (why not just use GA4?) AND the trust
           *  signal (who's behind this) in one read so the eye gets
           *  to the CTA quickly. Earlier draft had 5 separate text
           *  blocks — too many voices competing for scan attention.
           *  Sked voice: plain, specific, numbers over superlatives. */}
          <p
            className="ts-reveal mt-6 max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700"
            style={{ animationDelay: "140ms" }}
          >
            Connect GA4. One minute later, a social-first report, plus a UTM Health score that flags
            the broken attribution costing you campaigns.
          </p>
          {/* SKE-3438 item 2 — primary CTA stays prominent; the
           *  "Audit UTMs without signing in" secondary is a small link
           *  BELOW the primary so the click decision is unambiguous.
           *  The privacy chip (item 5) sits inline next to the primary
           *  so it's read on the same visual scan as the CTA. */}
          {/* Primary CTA + secondary link paired on one row (items-center so
           *  the link sits on the button's centre line), a clean two-action
           *  hierarchy. The privacy reassurance moves to its own line below —
           *  as a small pill beside the large button it read as misaligned. */}
          <div
            className="ts-reveal mt-9 flex flex-wrap items-center gap-x-5 gap-y-3"
            style={{ animationDelay: "210ms" }}
          >
            <Link to="/app" className="ts-cta ts-cta-lg">
              Get free report <span className="ts-cta-arrow">→</span>
            </Link>
            <Link to="/utm-health-check" className="ts-link">
              Score my UTMs, free <span className="ts-link-arrow">→</span>
            </Link>
          </div>
          {/* SKE-3438 item 5 — privacy promise as a visible reassurance (the
           *  conversion-blocker for privacy-conscious marketers). Wording is
           *  literally accurate: GA4 data isn't persisted row-level; a derived
           *  report is cached briefly. Tooltip carries the full explanation. */}
          <div className="ts-reveal mt-4" style={{ animationDelay: "280ms" }}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex cursor-help items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                  >
                    <ShieldCheckIcon />
                    Read-only access · No raw data stored
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  We request read-only Google Analytics access and never store your raw GA4 data. A
                  derived report is cached briefly to render your snapshot, then expires.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="ts-reveal relative" style={{ animationDelay: "240ms" }}>
          <HeroReportCard />
          <p className="mt-9 text-sm">
            <Link to="/sample" className="ts-link">
              See the full sample report <span className="ts-link-arrow">→</span>
            </Link>
          </p>
        </div>
      </div>
    </header>
  );
}

function HeroReportCard() {
  return (
    <div className="relative rounded-[20px] border border-neutral-950/5 bg-white p-7 shadow-[0_28px_60px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
      <div className="mb-[18px]">
        <p className="text-xs font-semibold text-purple-700">{SAMPLE_REPORT_LABEL}</p>
        <p className="mt-2 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-600">
          Last 90 days
        </p>
        <p className="mt-1 font-display text-xl font-bold tracking-[-0.01em] text-ink">
          Social overview
        </p>
      </div>
      <dl className="mb-4 grid grid-cols-3 gap-4 border-y border-neutral-100 py-[10px] pb-[18px]">
        {SAMPLE_HEADLINE_METRICS.map((m, i) => (
          <div key={m.label}>
            <dt className="text-xs font-medium text-neutral-600">{m.label}</dt>
            <dd className="mt-1 font-display text-[26px] font-[900] leading-none tracking-[-0.02em] text-ink tabular-nums">
              <CountUp value={m.value} delay={0.45 + i * 0.08} />
            </dd>
          </div>
        ))}
      </dl>
      <div className="grid grid-cols-[84px_1fr_56px] items-center gap-x-3.5 gap-y-2.5 text-[13px]">
        {samplePlatformBars(4).map((row, i) => (
          <div key={row.platform} className="contents">
            <div className="font-medium text-neutral-700">{row.platform}</div>
            <div
              aria-hidden="true"
              className="relative h-2 overflow-hidden rounded-full bg-neutral-100"
            >
              <motion.span
                className="block h-full origin-left rounded-full bg-purple-500"
                style={{ width: `${row.pct}%` }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.6, delay: 0.55 + i * 0.08, ease: EASE }}
              />
            </div>
            <div className="text-right font-bold text-ink tabular-nums">
              {row.sessions.toLocaleString("en-US")}
            </div>
          </div>
        ))}
      </div>
      <motion.div
        className="absolute -bottom-5 -right-6 min-w-[160px] rounded-2xl border border-neutral-950/5 bg-white px-[18px] py-3.5 shadow-[0_18px_36px_-10px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]"
        initial={{ opacity: 0, scale: 0.92, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.62, ease: EASE }}
      >
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.09em] text-neutral-600">
          UTM Health score
        </p>
        <p className="mt-1 font-display text-[44px] font-[900] leading-none tracking-[-0.025em] text-ink tabular-nums">
          <CountUp value={SAMPLE_HEADLINE.utmHealthScore} delay={0.72} />
          <span className="ml-0.5 text-base font-bold text-neutral-400">/100</span>
        </p>
      </motion.div>
    </div>
  );
}

function Marquee() {
  return (
    <div className="border-y border-neutral-100 bg-white py-6">
      <Reveal className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-3.5 px-8 text-[13px] text-neutral-600">
        {/* SKE-3472 — logo is now a link to skedsocial.com (new tab) so
         *  brand attribution is clickable. Underline-offset + hover
         *  ring from the utility class kept minimal — the image sits
         *  as the anchor's visual affordance, no extra chrome needed. */}
        <a
          href={LANDING_MARQUEE_LOGO_HREF}
          target="_blank"
          rel="noreferrer"
          aria-label="Sked Social (opens in new tab)"
          className="inline-flex shrink-0 rounded-sm focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
        >
          <img
            src={withBase("/sked-logo.webp")}
            alt="Sked Social"
            className="h-5 w-auto"
            width="92"
            height="20"
          />
        </a>
        <span>
          <em className="font-serif italic font-semibold tracking-[-0.005em] text-ink">
            Sked Social
          </em>{" "}
          is the leading social media management tool used by leading brands like Qantas, IHG and
          The Iconic.
        </span>
        <a
          href={LANDING_MARQUEE_LEARN_MORE_HREF}
          target="_blank"
          rel="noreferrer"
          className="ts-link ml-1 inline-flex items-center gap-1"
        >
          Learn more <span className="ts-link-arrow">→</span>
        </a>
      </Reveal>
    </div>
  );
}

// Compact, illustrative mini-previews — one per read, so the section *shows*
// each read instead of only describing it. Decorative (aria-hidden); the card
// copy carries the meaning for screen readers. Mock sample data, in the same
// spirit as the hero report card.
function SourcesViz() {
  const rows = [
    { label: "Instagram", pct: 92 },
    { label: "TikTok", pct: 61 },
    { label: "LinkedIn", pct: 44 },
    { label: "Facebook", pct: 27 },
  ];
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3">
          <span className="w-16 shrink-0 text-[11px] font-medium text-neutral-500">{r.label}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
            <span
              className="block h-full rounded-full bg-purple-500"
              style={{ width: `${r.pct}%` }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function ContentViz() {
  const rows = [
    { path: "/spring-drop", mult: "2.4×" },
    { path: "/pricing", mult: "1.8×" },
    { path: "/blog/utm-guide", mult: "1.3×" },
  ];
  return (
    <div aria-hidden="true" className="flex flex-col gap-2.5">
      {rows.map((r) => (
        <div key={r.path} className="flex items-center justify-between gap-3">
          <span className="truncate text-[11px] text-neutral-500">{r.path}</span>
          <span className="shrink-0 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-purple-700">
            {r.mult}
          </span>
        </div>
      ))}
    </div>
  );
}

function AttributionViz() {
  const params = [
    { key: "utm_source", ok: true },
    { key: "utm_medium", ok: false },
    { key: "utm_campaign", ok: true },
    { key: "utm_content", ok: false },
  ];
  return (
    <div aria-hidden="true" className="mt-6 grid grid-cols-2 gap-2.5">
      {params.map((p) => (
        <div
          key={p.key}
          className="flex items-center gap-2 rounded-lg border border-purple-100 bg-white/70 px-2.5 py-2 text-[12px] text-neutral-700"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={"shrink-0 " + (p.ok ? "text-green-600" : "text-amber-500")}
          >
            {p.ok ? <path d="m5 13 4 4L19 7" /> : <path d="M6 6l12 12M18 6 6 18" />}
          </svg>
          <span className="truncate">{p.key}</span>
        </div>
      ))}
    </div>
  );
}

interface Cut {
  icon: React.ReactNode;
  title: string;
  body: string;
  tag: string;
  pink?: boolean;
  viz: React.ReactNode;
}

const CUTS: Cut[] = [
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 4 6 4 9s-1.5 6.4-4 9c-2.5-2.6-4-6-4-9s1.5-6.4 4-9Z" />
      </svg>
    ),
    title: "Where your traffic actually comes from.",
    body: "Instagram, TikTok, LinkedIn, Facebook, X, YouTube, Pinterest, Threads, ranked by sessions, engaged users and key events. Plus a country- and city-level geography cut, filtered to social sessions only so bot traffic doesn't pollute the read.",
    tag: "Sources",
    viz: <SourcesViz />,
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
    title: "Which pages social actually converts.",
    body: "Landing-page winners, ranked by sessions-per-user from social traffic, not just which got the most clicks. Long URLs stay copyable.",
    tag: "Content",
    viz: <ContentViz />,
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 12h4l2-4 4 8 2-4h6" />
      </svg>
    ),
    title: "Whether your UTMs are helping or hurting.",
    body: "A score out of 100 with every deduction explained. Flagged rows exportable to CSV, so you can fix them in whatever tool published them.",
    tag: "Attribution",
    pink: true,
    viz: <AttributionViz />,
  },
];

function ThreeReads() {
  const feature = CUTS.find((c) => c.pink);
  const supporting = CUTS.filter((c) => !c.pink);
  return (
    <section id="cuts" className="py-24 md:py-28">
      <div className="mx-auto max-w-[1440px] px-8">
        {/* SKE-3472 — heading-group wrapper has no max-w (per the ui
         *  skill's heading-group rules — constrain each text element
         *  individually, never the wrapper). The h2 uses max-w-[30ch]
         *  for text-5xl per the same guidance, which breaks the
         *  heading into a balanced 2-line wrap instead of the cramped
         *  4-line wrap the previous max-w-[22ch] produced on desktop.
         *  `md:text-[48px]` dropped because text-5xl is already 48px. */}
        <Reveal className="mb-12 text-center">
          <h2 className="mx-auto max-w-[20ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance">
            Three reads that answer what social people get asked.
          </h2>
          <p className="mx-auto mt-[18px] max-w-[54ch] text-[19px] leading-[1.55] text-neutral-700">
            Every read is shaped for a social team's narrative, not a BI analyst's. Plain language
            for empty states, broken UTMs and platform quirks.
          </p>
        </Reveal>
        {/* Bento: the signature Attribution read as a tall wash-tinted feature tile
            on the left, the two supporting reads stacked beside it on the right. */}
        <motion.div
          className="grid gap-4 lg:grid-cols-2 lg:items-stretch"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {feature && (
            <motion.div
              variants={staggerItem}
              whileHover={{ y: -2, transition: { duration: 0.2, ease: EASE } }}
              className="group flex flex-col justify-center rounded-[18px] border border-purple-100 bg-[#F4F2FF] p-8 transition-[border-color,box-shadow] duration-200 hover:border-purple-500/25 hover:shadow-[0_18px_44px_-20px_rgba(79,49,255,0.28)] md:p-10"
            >
              <div className="flex items-center gap-2.5 text-pink-600">
                <span
                  className="inline-flex size-10 items-center justify-center rounded-xl bg-pink-50"
                  aria-hidden="true"
                >
                  {feature.icon}
                </span>
                <span className="font-sans text-[11px] font-bold uppercase tracking-[0.16em]">
                  {feature.tag}
                </span>
              </div>
              <h3 className="mt-6 max-w-[18ch] font-display text-2xl font-bold tracking-[-0.015em] text-ink">
                {feature.title}
              </h3>
              <p className="mt-3 max-w-[42ch] text-[15px] leading-[1.6] text-neutral-600">
                {feature.body}
              </p>
              {feature.viz}
            </motion.div>
          )}
          <div className="grid gap-4">
            {supporting.map((cut) => (
              <motion.div
                key={cut.title}
                variants={staggerItem}
                whileHover={{ y: -2, transition: { duration: 0.2, ease: EASE } }}
                className="group flex flex-col gap-3 rounded-[18px] border border-neutral-100 bg-white p-7 transition-[border-color,box-shadow] duration-200 hover:border-purple-500/20 hover:shadow-[0_14px_34px_-16px_rgba(16,24,40,0.18)]"
              >
                <div className="flex items-center gap-2.5 text-purple-600">
                  <span
                    className="inline-flex size-9 items-center justify-center rounded-lg bg-purple-50"
                    aria-hidden="true"
                  >
                    {cut.icon}
                  </span>
                  <span className="font-sans text-[11px] font-bold uppercase tracking-[0.16em]">
                    {cut.tag}
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-[-0.012em] text-ink">
                  {cut.title}
                </h3>
                <p className="text-sm leading-[1.55] text-neutral-600">{cut.body}</p>
                <div className="mt-1">{cut.viz}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <motion.p
          className="mx-auto mt-10 max-w-[56ch] text-center text-[18px] font-medium leading-[1.55] text-neutral-800"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          That's the <em className="font-serif font-semibold italic text-purple-600">whole</em>{" "}
          report. No dashboards with twelve tabs. If a read doesn't change what you'd do on Monday,
          we leave it out.
        </motion.p>
      </div>
    </section>
  );
}

function UtmHealthStage() {
  return (
    <section
      id="health"
      className="py-24 md:py-28"
      style={{ background: "linear-gradient(180deg, #fff 0%, #F4F2FF 100%)" }}
    >
      <div className="mx-auto max-w-[1440px] px-8">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <Reveal className="md:order-2">
            <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
              UTM Health Snapshot
            </span>
            <h2 className="max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance md:text-[48px]">
              Find out whether your UTMs are helping or hurting.
            </h2>
            <p className="mt-[18px] max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
              A score out of 100. Every deduction explained. Flagged rows downloadable as CSV. And a
              taxonomy builder seeded from your audit findings, so next campaign ships clean.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link
                to="/utm-health-check"
                className="group inline-flex h-12 items-center gap-2 rounded-full border border-purple-200 bg-white px-6 text-[15px] font-semibold text-purple-700 shadow-xs transition-all duration-200 hover:-translate-y-px hover:border-purple-500/40 hover:bg-purple-50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                Score my UTMs, free
                <span
                  aria-hidden="true"
                  className="transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                >
                  →
                </span>
              </Link>
              <span className="text-sm text-neutral-600">No sign-in needed.</span>
            </div>
          </Reveal>
          <div className="md:order-1">
            <UtmHealthDemoCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function UtmHealthDemoCard() {
  const issues = [
    { label: "Inconsistent source casing", n: 36, tone: "severe" as const },
    { label: "Missing utm_medium", n: 9, tone: "warn" as const },
    { label: "Non-canonical utm values", n: 7, tone: "warn" as const },
    { label: "Missing utm_content on carousels", n: 21, tone: "ok" as const },
  ];
  const toneDot: Record<(typeof issues)[number]["tone"], string> = {
    severe: "bg-red-500",
    warn: "bg-yellow-500",
    ok: "bg-green-500",
  };
  return (
    <div className="rounded-[20px] border border-neutral-950/5 bg-white p-7 shadow-[0_22px_44px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
      <div className="mb-2.5 flex items-baseline justify-between">
        <div>
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-600">
            UTM Health · Last 90 days
          </p>
          <p className="mt-1 font-display text-lg font-bold tracking-[-0.008em] text-ink">
            Sked Social
          </p>
        </div>
      </div>
      <div className="mt-1.5 mb-3.5 flex items-baseline gap-2.5">
        <p className="font-display text-[72px] font-[900] leading-none tracking-[-0.03em] text-ink tabular-nums">
          <CountUp value={68} startOnView duration={1.2} />
        </p>
        <span className="rounded-full bg-yellow-50 px-2.5 py-1.5 text-[13px] font-bold text-yellow-700">
          Needs work
        </span>
      </div>
      <div aria-hidden="true" className="mb-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <motion.div
          className="h-full w-[68%] origin-left rounded-full bg-gradient-to-r from-yellow-500 to-pink-500"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.9, ease: EASE }}
        />
      </div>
      <motion.ul
        role="list"
        className="flex flex-col gap-3"
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
      >
        {issues.map((i) => (
          <motion.li
            key={i.label}
            variants={staggerItem}
            className="flex items-center justify-between gap-3.5 text-[13.5px]"
          >
            <div className="flex items-center gap-2.5 text-neutral-700">
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${toneDot[i.tone]}`}
              />
              {i.label}
            </div>
            <span className="text-xs font-semibold text-neutral-600 tabular-nums">{i.n} rows</span>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  );
}

// Item 8 (launch hardening) — renders nothing until the launch-day flag
// flips. A thin strip under the nav, not a banner: the page's primary
// CTA hierarchy must not change on launch day.
function ProductHuntStrip() {
  if (!SHOW_PRODUCT_HUNT_STRIP) return null;
  return (
    <div className="border-b border-neutral-100 bg-[#fff8f3] py-2.5 text-center text-[13px] font-medium text-neutral-700">
      We're live on Product Hunt today.{" "}
      <a href={PRODUCT_HUNT_URL} target="_blank" rel="noreferrer" className="ts-link">
        Support the launch <span className="ts-link-arrow">→</span>
      </a>
    </div>
  );
}

// Handover item 2 — the agency use case the multi-property picker
// already supports but the copy never named. Section mirrors the
// UtmHealthStage cadence (eyebrow pill + display h2 + paragraph +
// separator line + outline CTA); pink stays reserved for the page's
// primary "Get free report" action.
function AgencySection() {
  return (
    <section id="agencies" className="py-24 md:py-28">
      <div className="mx-auto max-w-[1440px] px-8">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <Reveal>
            <span className="mb-[14px] inline-block rounded-full border border-purple-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-purple-600">
              For agencies
            </span>
            <h2 className="max-w-[22ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance">
              Run it per client. Share the branded report.
            </h2>
            <p className="mt-[18px] max-w-[56ch] text-[19px] leading-[1.55] text-neutral-700">
              The property picker lists every GA4 property your Google login can reach. Switch
              clients in two clicks, pull the same social-first read for each, and send a branded
              share link or PDF that survives the Monday meeting.
            </p>
            <p className="mt-5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm font-medium text-neutral-700">
              <span>Every client property, one connect</span>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-neutral-300" />
              <span>Branded PDFs + share links</span>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-neutral-300" />
              <span>A UTM scorecard per client</span>
            </p>
            <div className="mt-7">
              <Link
                to={LANDING_AGENCY_APP_LINK}
                className="group inline-flex h-12 items-center gap-2 rounded-full border border-purple-200 bg-white px-6 text-[15px] font-semibold text-purple-700 shadow-xs transition-all duration-200 hover:-translate-y-px hover:border-purple-500/40 hover:bg-purple-50 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                Run it on a client property
                <span
                  aria-hidden="true"
                  className="transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                >
                  →
                </span>
              </Link>
            </div>
          </Reveal>
          <div>
            <AgencyPropertiesCard />
          </div>
        </div>
      </div>
    </section>
  );
}

// Illustrative mini-preview in the same spirit as HeroReportCard /
// UtmHealthDemoCard — mock client properties, decorative only.
function AgencyPropertiesCard() {
  const clients = [
    { name: "Aurora Hotels · GA4", chip: "Report shared Mon" },
    { name: "Peak Cycles · GA4", chip: "PDF sent to client" },
    { name: "Field & Flour · GA4", chip: "UTM score 74" },
  ];
  return (
    <div className="rounded-[20px] border border-neutral-950/5 bg-white p-7 shadow-[0_22px_44px_-18px_rgba(16,24,40,0.18),0_2px_4px_rgba(16,24,40,0.04)]">
      <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-600">
        Property picker
      </p>
      <p className="mt-1 font-display text-lg font-bold tracking-[-0.008em] text-ink">
        Every client, one login
      </p>
      <ul role="list" className="mt-4 divide-y divide-neutral-100" aria-hidden="true">
        {clients.map((c) => (
          <li key={c.name} className="flex items-center justify-between gap-3 py-3">
            <span className="truncate text-[14px] font-medium text-neutral-800">{c.name}</span>
            <span className="shrink-0 rounded-full bg-purple-50 px-2.5 py-1 text-[11px] font-bold text-purple-700">
              {c.chip}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Handover item 2 + item 7 SEO — visible FAQ backing the FAQPage
// JSON-LD the worker injects on "/". Content lives in
// @shared/marketing/landing-faq so the structured data and this render
// can never drift apart. Native <details> keeps it dependency-free and
// crawlable (answers are in the DOM regardless of open state).
function FaqSection() {
  return (
    <section id="faq" className="border-t border-neutral-100 bg-white py-24 md:py-28">
      <div className="mx-auto max-w-[840px] px-8">
        <Reveal className="mb-10 text-center">
          <h2 className="font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance">
            Questions, answered.
          </h2>
        </Reveal>
        <div className="divide-y divide-neutral-100 rounded-[18px] border border-neutral-100 bg-white">
          {LANDING_FAQ.map((item) => (
            <details key={item.question} className="group px-6 py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-[17px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
                {item.question}
                <span
                  aria-hidden="true"
                  className="text-neutral-400 transition-transform duration-200 group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 max-w-[68ch] text-[15px] leading-[1.6] text-neutral-600">
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSked() {
  // Distilled to match the ThreeReads / UtmHealthStage cadence (eyebrow +
  // headline + one paragraph + CTA) — the old copy carried two paragraphs, a
  // "see/do" line and a 3-item bullet list that restated the paragraph. The
  // workspace image is now the dominant element: oversized and bleeding past
  // the 1200px container's right edge via a transform (no reflow), with the
  // section clipping overflow so there's no horizontal scrollbar.
  return (
    <section id="about" className="relative bg-peach py-24 md:py-28 [overflow-x:clip]">
      <div className="mx-auto max-w-[1440px] px-8">
        <div className="grid items-center gap-10 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.h2
              variants={staggerItem}
              className="max-w-[20ch] font-display text-5xl font-[900] leading-[1.05] tracking-[-0.025em] text-ink text-balance"
            >
              Built by the team that cares about social enough to build{" "}
              <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
                this
              </span>
              .
            </motion.h2>
            <motion.p
              variants={staggerItem}
              className="mt-[18px] max-w-[44ch] text-[17px] leading-[1.6] text-neutral-700"
            >
              Sked Social is the connected workspace where 10,000+ marketers plan, publish, listen
              and report. One calendar for every network, with the UTM hygiene that keeps a tool
              like Traffic Snapshot honest.
            </motion.p>
            <motion.p
              variants={staggerItem}
              className="mt-6 max-w-[40ch] text-[19px] font-medium leading-[1.5] text-ink"
            >
              Here you <em className="font-serif font-semibold italic text-purple-600">see</em>{" "}
              attribution. In Sked Social you{" "}
              <em className="font-serif font-semibold italic text-purple-600">do</em> the work.
            </motion.p>
            <motion.p
              variants={staggerItem}
              className="mt-5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm font-medium text-neutral-700"
            >
              <span>Plan, approve, publish</span>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-neutral-300" />
              <span>Listen and respond</span>
              <span aria-hidden="true" className="h-3 w-px shrink-0 bg-neutral-300" />
              <span>Report, automatic</span>
            </motion.p>
            <motion.div variants={staggerItem} className="mt-7 flex flex-wrap items-center gap-4">
              <a
                href={LANDING_MID_EXPLORE_HREF}
                target="_blank"
                rel="noreferrer"
                className="ts-cta"
              >
                Explore Sked Social <span className="ts-cta-arrow">→</span>
              </a>
            </motion.div>
          </motion.div>
          {/* Image is flush to the content container's right edge: justify-self-end
              puts it at the content-box edge, and translate-x-8 cancels the px-8
              padding so it reaches the container max-width edge. Below the 1536
              cap that edge is the viewport edge (looks bled); above it, content
              letterboxes and the image stays flush to the box edge rather than
              chasing the far viewport edge. Translate on the wrapper, not the
              <img>, so it doesn't fight the Motion entrance transform. */}
          <div className="relative md:translate-x-8 md:justify-self-end">
            <motion.img
              src={withBase("/sked-workspace.webp")}
              alt="The Sked Social workspace: a Scratchpad of post ideas beside a Workspace board of planned and scheduled campaign posts, with a Plan, Draft and Schedule toolbar."
              className="block w-full max-w-none md:w-[min(58vw,860px)]"
              width="1800"
              height="1244"
              loading="lazy"
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, ease: EASE }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCtaBand() {
  return (
    <section className="py-24 md:py-28">
      <div className="mx-auto max-w-[1440px] px-8">
        <Reveal className="mx-auto flex max-w-[720px] flex-col items-center rounded-[28px] bg-[#0B1120] px-8 py-16 text-center text-white shadow-[0_40px_80px_-30px_rgba(79,49,255,0.4)] md:px-12 md:py-20">
          <SkedMark
            className="mb-7 h-14 w-14 drop-shadow-[0_10px_24px_rgba(79,49,255,0.35)]"
            aria-hidden="true"
          />
          <h2 className="max-w-[20ch] font-display text-[40px] font-[900] leading-[1.04] tracking-[-0.025em] text-balance md:text-[46px]">
            One minute from{" "}
            <span className="font-serif italic font-semibold text-pink-300">connect</span> to
            clarity.
          </h2>
          <p className="mt-3.5 max-w-[52ch] text-[17px] leading-[1.55] text-white/70">
            Bring your GA4. Leave with a report a CMO will forward and a UTM audit that pays for
            itself on the next campaign. Branded PDF, CSV and weekly-digest email included.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-[18px]">
            <Link to="/app" className="ts-cta ts-cta-lg">
              Get free report <span className="ts-cta-arrow">→</span>
            </Link>
            <a
              href={LANDING_FINAL_CTA_PRICING_HREF}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-purple-200 no-underline hover:text-purple-100"
            >
              See Sked Social pricing <span>→</span>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="py-14 pb-20">
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-6 px-8">
        <Link to="/" className="flex items-center gap-2.5 text-ink no-underline">
          <SkedMark className="size-7" aria-hidden="true" />
          <span className="font-display text-[15px] font-[900] tracking-[-0.01em] text-ink">
            Traffic Snapshot
          </span>
        </Link>
        <span className="text-[13px] text-neutral-600">
          <b className="font-bold text-ink">Traffic Snapshot</b> · a free report from Sked Social ·{" "}
          <i className="font-serif italic font-medium text-purple-700">social-first</i> analytics,
          built for marketers.
        </span>
        <div className="flex-1" />
        <a
          href="https://skedsocial.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Privacy
        </a>
        <a
          href="https://skedsocial.com/terms-of-service"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          Terms
        </a>
        <a
          href={LANDING_SITE_FOOTER_HREF}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] text-neutral-600 no-underline hover:text-purple-700"
        >
          skedsocial.com ↗
        </a>
      </div>
    </footer>
  );
}
